import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card } from '@ui'
import {
  Chart,
  LineController,
  BarController,
  LineElement,
  BarElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
} from 'chart.js'
import { getAnalytics } from '../api.js'

Chart.register(
  LineController, BarController, LineElement, BarElement, PointElement,
  LinearScale, CategoryScale, Tooltip,
)

const PERIODS = [
  { id: '7d', label: '7d' },
  { id: '30d', label: '30d' },
  { id: '90d', label: '90d' },
  { id: 'all', label: 'All' },
]

export function Analytics() {
  const nav = useNavigate()
  const [period, setPeriod] = useState('30d')
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    getAnalytics(period)
      .then((d) => { if (!cancelled) { setData(d); setError(null) } })
      .catch((e) => { if (!cancelled) setError(e.message || 'Failed to load analytics') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [period])

  if (error) {
    return <p style={{ color: 'var(--danger-text)' }}>{error}</p>
  }
  if (!data) {
    return <p>Loading…</p>
  }

  const dim = loading ? { opacity: 0.5, transition: 'opacity 0.15s' } : {}

  return (
    <div style={dim}>
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>Analytics</h2>
          <p style={styles.sub}>Platform-wide activity and org health</p>
        </div>
        <PeriodTabs value={period} onChange={setPeriod} />
      </div>

      <KpiRow kpis={data.kpis} nav={nav} />

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16, marginBottom: 16 }}>
        <GrowthChart series={data.growthSeries} />
        <NewOrgsBar series={data.newOrgsPerMonth} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
        <InviteFunnel funnel={data.inviteFunnel} />
        <ActivationTime act={data.activationTime} onOrphanClick={() => nav('/orgs?filter=orphaned')} />
        <CapUsage rows={data.capUsage} onRow={(id) => nav(`/orgs/${id}`)} />
      </div>
    </div>
  )
}

function PeriodTabs({ value, onChange }) {
  return (
    <div style={styles.tabs} role="tablist" aria-label="Period">
      {PERIODS.map((p) => {
        const active = p.id === value
        return (
          <button
            key={p.id}
            role="tab"
            aria-selected={active}
            onClick={() => onChange(p.id)}
            style={{ ...styles.tab, ...(active ? styles.tabActive : {}) }}
          >
            {p.label}
          </button>
        )
      })}
    </div>
  )
}

function KpiRow({ kpis, nav }) {
  const newOrgsDelta = kpis.newOrgs > 0
    ? { text: `+${kpis.newOrgs} this period`, color: '#3B6D11' }
    : null
  const newUsersDelta = kpis.newUsers > 0
    ? { text: `+${kpis.newUsers} this period`, color: '#3B6D11' }
    : null

  const accDelta = kpis.inviteAcceptanceRate - kpis.prevInviteAcceptanceRate
  const accHint = accDelta > 0
    ? { text: `+${accDelta}% vs prev period`, color: '#3B6D11' }
    : accDelta < 0
      ? { text: `${accDelta}% vs prev period`, color: '#BA7517' }
      : null

  const orphanHint = kpis.orphanedOrgs > 0
    ? { text: 'no admin ever confirmed', color: '#BA7517' }
    : { text: 'all activated', color: '#3B6D11' }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 16 }}>
      <KpiCard label="Total orgs" value={kpis.totalOrgs} hint={newOrgsDelta} />
      <KpiCard label="Total users" value={kpis.totalUsers} hint={newUsersDelta} />
      <KpiCard
        label="Invite acceptance rate"
        value={`${kpis.inviteAcceptanceRate}%`}
        hint={accHint}
      />
      <KpiCard
        label="Orphaned orgs"
        value={kpis.orphanedOrgs}
        hint={orphanHint}
        onClick={kpis.orphanedOrgs > 0 ? () => nav('/orgs?filter=orphaned') : undefined}
      />
    </div>
  )
}

function KpiCard({ label, value, hint, onClick }) {
  return (
    <Card
      style={{ padding: '20px 24px', cursor: onClick ? 'pointer' : 'default' }}
      onClick={onClick}
    >
      <div style={{ fontSize: 13, opacity: 0.7, marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 32, fontWeight: 600, lineHeight: 1.1 }}>{value}</div>
      {hint && (
        <div style={{ fontSize: 12, color: hint.color, marginTop: 6 }}>{hint.text}</div>
      )}
    </Card>
  )
}

function GrowthChart({ series }) {
  const ref = useChart('line', {
    labels: series.map((p) => p.month),
    datasets: [
      {
        label: 'Users',
        data: series.map((p) => p.users),
        borderColor: '#378ADD',
        backgroundColor: '#378ADD',
      },
      {
        label: 'Orgs',
        data: series.map((p) => p.orgs),
        borderColor: '#639922',
        backgroundColor: '#639922',
        borderDash: [4, 2],
      },
    ],
  }, {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      x: { grid: { display: false }, ticks: { font: { size: 10 } } },
      y: { grid: { color: 'rgba(0,0,0,0.04)' }, ticks: { font: { size: 10 } } },
    },
    elements: { point: { radius: 0 }, line: { tension: 0.3 } },
  })
  return (
    <Card style={{ padding: 16 }}>
      <div style={styles.chartTitle}>Growth over time</div>
      <div style={{ height: 130 }}><canvas ref={ref} /></div>
      <div style={styles.legend}>
        <LegendDot color="#378ADD" label="Users" />
        <LegendDash color="#639922" label="Orgs" />
      </div>
    </Card>
  )
}

function NewOrgsBar({ series }) {
  const ref = useChart('bar', {
    labels: series.map((p) => p.month),
    datasets: [{
      data: series.map((p) => p.count),
      backgroundColor: '#639922',
      borderRadius: 3,
    }],
  }, {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      x: { grid: { display: false }, ticks: { font: { size: 10 } } },
      y: { beginAtZero: true, ticks: { stepSize: 1, font: { size: 10 } } },
    },
  })
  return (
    <Card style={{ padding: 16 }}>
      <div style={styles.chartTitle}>New orgs / month</div>
      <div style={{ height: 130 }}><canvas ref={ref} /></div>
    </Card>
  )
}

function useChart(type, data, options) {
  const canvasRef = useRef(null)
  const chartRef = useRef(null)
  useEffect(() => {
    if (!canvasRef.current) return undefined
    chartRef.current = new Chart(canvasRef.current, { type, data, options })
    return () => { chartRef.current?.destroy() }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(data)])
  return canvasRef
}

function LegendDot({ color, label }) {
  return (
    <span style={styles.legendItem}>
      <span style={{ ...styles.legendSwatch, background: color, borderRadius: '50%' }} />
      {label}
    </span>
  )
}
function LegendDash({ color, label }) {
  return (
    <span style={styles.legendItem}>
      <span style={{ ...styles.legendSwatch, borderBottom: `2px dashed ${color}`, height: 0, width: 14 }} />
      {label}
    </span>
  )
}

function InviteFunnel({ funnel }) {
  const sent = funnel.sent || 0
  const pct = (v) => (sent ? Math.round(v / sent * 100) : 0)
  const rows = [
    { label: 'Sent', value: sent, color: '#639922' },
    { label: 'Accepted', value: funnel.accepted, color: '#639922' },
    { label: 'Pending', value: funnel.pending, color: '#BA7517' },
    { label: 'Expired / revoked', value: funnel.expiredOrRevoked, color: '#B4B2A9' },
  ]
  return (
    <Card style={{ padding: 16 }}>
      <div style={styles.chartTitle}>Invite funnel</div>
      {rows.map((r) => (
        <div key={r.label} style={{ marginBottom: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
            <span>{r.label}</span>
            <span style={{ opacity: 0.7 }}>{r.value} ({pct(r.value)}%)</span>
          </div>
          <div style={styles.barTrack}>
            <div style={{ ...styles.barFill, width: `${pct(r.value)}%`, background: r.color }} />
          </div>
        </div>
      ))}
    </Card>
  )
}

function ActivationTime({ act, onOrphanClick }) {
  return (
    <Card style={{ padding: 16 }}>
      <div style={styles.chartTitle}>Time to first activation</div>
      <div style={{ fontSize: 28, fontWeight: 600 }}>{act.medianHours}h</div>
      <div style={{ fontSize: 11, opacity: 0.7, marginBottom: 12 }}>
        median, org creation → first confirmed admin
      </div>
      <div style={{ borderTop: '1px solid var(--border)', paddingTop: 10 }}>
        <ActRow label="under 4h" value={act.under4h} color={act.under4h > 0 ? '#3B6D11' : undefined} />
        <ActRow label="4h – 48h" value={act.between4and48h} />
        <ActRow label="over 48h" value={act.over48h} color={act.over48h > 0 ? '#BA7517' : undefined} />
        <ActRow
          label="never activated"
          value={act.neverActivated}
          color={act.neverActivated > 0 ? '#E24B4A' : undefined}
          onClick={act.neverActivated > 0 ? onOrphanClick : undefined}
        />
      </div>
    </Card>
  )
}

function ActRow({ label, value, color, onClick }) {
  const labelStyle = {
    fontSize: 12,
    cursor: onClick ? 'pointer' : 'default',
    textDecoration: onClick ? 'underline' : 'none',
  }
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0' }}>
      <span style={labelStyle} onClick={onClick}>{label}</span>
      <span style={{ fontSize: 12, color, fontWeight: color ? 600 : 400 }}>{value} orgs</span>
    </div>
  )
}

function CapUsage({ rows, onRow }) {
  return (
    <Card style={{ padding: 16 }}>
      <div style={styles.chartTitle}>User cap usage</div>
      {rows.length === 0 ? (
        <p style={{ fontSize: 12, opacity: 0.7 }}>All orgs well within their user limits.</p>
      ) : (
        rows.map((r) => {
          const color = r.pct >= 80 ? '#E24B4A' : r.pct >= 60 ? '#BA7517' : '#639922'
          return (
            <div
              key={r.orgId}
              onClick={() => onRow(r.orgId)}
              style={{ marginBottom: 10, cursor: 'pointer' }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 2 }}>
                <span>{r.name}</span>
                <span style={{ color, fontWeight: 600 }}>{r.pct}%</span>
              </div>
              <div style={{ fontSize: 11, opacity: 0.7, marginBottom: 4 }}>{r.used} / {r.max} users</div>
              <div style={styles.barTrack}>
                <div style={{ ...styles.barFill, width: `${Math.min(r.pct, 100)}%`, background: color }} />
              </div>
            </div>
          )
        })
      )}
    </Card>
  )
}

const styles = {
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 20,
  },
  title: { fontSize: 20, fontWeight: 600, marginBottom: 2 },
  sub: { fontSize: 12, color: 'var(--text-secondary)' },
  tabs: { display: 'inline-flex', border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' },
  tab: {
    padding: '6px 12px',
    border: 'none',
    background: 'transparent',
    fontSize: 12,
    cursor: 'pointer',
    color: 'var(--text-secondary)',
  },
  tabActive: { background: 'var(--border)', color: 'var(--text-primary)', fontWeight: 600 },
  chartTitle: { fontSize: 13, fontWeight: 600, marginBottom: 10 },
  barTrack: { width: '100%', height: 6, background: 'var(--border)', borderRadius: 3, overflow: 'hidden' },
  barFill: { height: '100%', borderRadius: 3 },
  legend: { display: 'flex', gap: 16, marginTop: 8, fontSize: 11, color: 'var(--text-secondary)' },
  legendItem: { display: 'inline-flex', alignItems: 'center', gap: 6 },
  legendSwatch: { display: 'inline-block', width: 10, height: 10 },
}
